import { ONE_SECOND_MS } from '@petmas/shared';

/**
 * Format a number as Peruvian Soles currency
 */
export function formatPrice(amount: number): string {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
  }).format(amount);
}

/**
 * Format a discount percentage
 */
export function formatDiscount(percent: number): string {
  return `-${Math.round(percent)}%`;
}

/**
 * Format a date in Spanish locale
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('es-PE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

const MINUTE = 60;
const HOUR = 3600;
const DAY = 86400;
const MONTH = 2592000;

/**
 * Format a relative time (e.g., "hace 2 dias")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffInSeconds = Math.floor((Date.now() - d.getTime()) / ONE_SECOND_MS);

  if (diffInSeconds >= MONTH) {
    return formatDate(d);
  }

  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });

  if (diffInSeconds < MINUTE) {
    return rtf.format(-diffInSeconds, 'second');
  }
  if (diffInSeconds < HOUR) {
    return rtf.format(-Math.floor(diffInSeconds / MINUTE), 'minute');
  }
  if (diffInSeconds < DAY) {
    return rtf.format(-Math.floor(diffInSeconds / HOUR), 'hour');
  }
  return rtf.format(-Math.floor(diffInSeconds / DAY), 'day');
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

/**
 * Generate a slug from a string
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}
